/**
 * People → PROS — round-3 handoff §3 (docs/plans/2026-09-header-host-pros/handoff/README.md).
 * The pure parts: the eight category tiles over the API's craft groups, the MOST BOOKED chips,
 * the note chip, the distance label, and the URL ↔ state mapping.
 *
 * Categories are the API's craft groups (ProCrafts.cs) — the eight the handoff draws. PLACES
 * and SHOPS are not categories any more; the INCLUDE PLACES & SHOPS switch controls them.
 * PLANNING and OTHER have no tile and are reached by text search ("planner").
 */
export const CATEGORIES = [
  { key: 'sound-show', label: 'SOUND & SHOW', dot: '#FF00FF' },
  { key: 'food-drink', label: 'FOOD & DRINK', dot: '#FCAA6D' },
  { key: 'staff', label: 'STAFF', dot: '#22ECFB' },
  { key: 'space-gear', label: 'SPACE & GEAR', dot: '#127BFD' },
  { key: 'look-feel', label: 'LOOK & FEEL', dot: '#FF33C6' },
  { key: 'capture', label: 'CAPTURE', dot: '#B5B1FF' },
  { key: 'kids-wellness', label: 'KIDS & WELLNESS', dot: '#FAE1B4' },
  { key: 'logistics', label: 'LOGISTICS', dot: '#9CA3AF' },
];

/** PLANNING A PARTY? MOST BOOKED — label → craft slug (and the group it lives in). */
export const MOST_BOOKED = [
  { label: 'DJ', craft: 'dj', group: 'sound-show' },
  { label: 'Bartender', craft: 'bartender', group: 'food-drink' },
  { label: 'Private chef', craft: 'chef', group: 'food-drink' },
  { label: 'Tables & chairs', craft: 'furniture-rentals', group: 'space-gear' },
  { label: 'Security', craft: 'security', group: 'staff' },
  { label: 'Portable washrooms', craft: 'portable-toilets', group: 'logistics' },
];

export const RADII = [10, 25, 50, 100];
export const DEFAULT_RADIUS = 50;
export const PAGE = 12;
const NEW_DAYS = 30;

export const isPlaceOrShop = (kind) => !!kind && kind !== 'bookable';

/** `IN TOWN` within a mile, else `N MI`; nothing without a point. */
export function distanceLabel(miles) {
  if (miles == null || !Number.isFinite(miles)) return '';
  return miles <= 1 ? 'IN TOWN' : `${Math.round(miles)} MI`;
}

/** The note chip on an ON PORTAL card: FROM $200 on cyan, NEW ON PORTAL on pink, or nothing. */
export function noteChip(pro, now = new Date()) {
  if (pro.fromPriceCents > 0) return { label: `FROM $${Math.round(pro.fromPriceCents / 100).toLocaleString('en-CA')}`, bg: '#E6FFFF' };
  if (pro.createdAt && (now - new Date(pro.createdAt)) / 86400000 < NEW_DAYS) return { label: 'NEW ON PORTAL', bg: '#FFCCE6' };
  return null;
}

/** `PENTICTON` from `Penticton, BC`. */
export const cityOf = (label) => (label ? label.split(',')[0].trim().toUpperCase() : '');

export function tintForName(name) {
  const tints = ['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FAE1B4', '#E6FFFF'];
  return tints[(name || '?').charCodeAt(0) % tints.length];
}

/** The filters as they live in the URL (beside `tab=pros`). */
export function readProsParams(params) {
  const r = parseInt(params.get('r') || '', 10);
  const lat = parseFloat(params.get('lat'));
  const lng = parseFloat(params.get('lng'));
  return {
    q: params.get('q') || '',
    group: params.get('cat') || '',
    craft: params.get('sub') || '',
    radius: RADII.includes(r) ? r : DEFAULT_RADIUS,
    places: params.get('places') === '1',
    city: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng, label: params.get('city') || 'NEAR HERE' } : null,
    anywhere: params.get('anywhere') === '1',
  };
}

/** `N PROS · WITHIN X MI OF CITY · FILTER`. */
export function summaryLine({ total, radius, city, group, craft, craftLabel }) {
  const parts = [`${total} ${total === 1 ? 'PRO' : 'PROS'}`];
  parts.push(city ? `WITHIN ${radius} MI OF ${cityOf(city.label)}` : 'ANYWHERE');
  if (craft) parts.push((craftLabel || craft).toUpperCase());
  else if (group) parts.push((CATEGORIES.find((c) => c.key === group)?.label) || group.toUpperCase());
  return parts.join(' · ');
}
